import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import { ApolloError } from '@apollo/client/errors'
import RoundButton from '@deltek/specification-client/core/Buttons/RoundButton'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import {
    ProductListingDetail,
    ProductListingDetailInput,
} from '@deltek/specification-client/core/Models/productListingDetail'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Backdrop from '@material-ui/core/Backdrop'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import { Theme, makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import { SaveProprietaryCategoriesByFamily } from 'bpm/api/graphQL/mutations/categoryToFamily.mutation'
import { CreateProductListingDetail } from 'bpm/api/graphQL/mutations/productListing.mutation'
import { FirmIdByDisplayNameQuery } from 'bpm/api/graphQL/queries/firm.queries'
import { GetAllProducts } from 'bpm/api/graphQL/queries/productSearch.queries'
import { ProductSearchResponse } from 'bpm/api/graphQL/types/product.types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { ListingStatus } from '../ProductDetailsPage/components/ListingHeaderVariants/ListingStatusVariant'
import {
    ADD_PRODUCT_ERROR_MESSAGE,
    ADD_PRODUCT_SUCCESS_MESSAGE,
    ADD_PROPRIETARY_CATEGORY_ERROR_MESSAGE,
    ADD_PROPRIETARY_CATEGORY_SUCCESS_MESSAGE,
    DUPLICATE_PRODUCT_MESSAGE,
} from '../constants'
import { default as AddProduct } from './AddProductForm'
import { AddProductForm, RadioType } from './AddProductTypes'
import {
    addProductDialogState,
    defaultAddProductDialogState,
    setProductDialogState,
} from './addProductFormHandler'

export const useStyles = makeStyles((theme: Theme) => ({
    actionContainer: {
        height: pxToRem(70),
        borderTop: '1px solid #C9C9C9',
        paddingRight: theme.spacing(4),
        marginTop: theme.spacing(4),
    },
    addProductButton: {
        width: pxToRem(140),
        height: pxToRem(41),
        color: theme.palette.secondary.light,
        borderRadius: 100,
        backgroundColor: '#13B5ea',
        fontSize: pxToRem(14),
        '&:hover': {
            backgroundColor: '#13B5ea',
            color: theme.palette.secondary.light,
        },
        '&[disabled]': {
            color: theme.palette.secondary.light,
            opacity: 0.5,
        },
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(2),
        color: theme.palette.grey[500],
    },
    title: {
        height: pxToRem(48),
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid #C9C9C9',
    },
    dialogcontent: {
        minWidth: pxToRem(960),
        minHeight: pxToRem(475),
    },
    dialogContainer: {
        overflowX: 'hidden',
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.secondary.light,
    },
}))

export const MODEL_MAX_LENGTH = 60
export const PRODUCT_NAME_MAX_LENGTH = 200
export const PREMIUM_PLUS = ListingLevel.Premium
export const FIRM_NAME_SEPARATOR = '%|%'

export interface AddNewProductModalProps {
    firmId?: String
}

const AddNewProductModal = ({ firmId }: AddNewProductModalProps) => {
    const classes = useStyles()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [isPremiumPlusProductType, setIsPremiumPlusProductType] = useState(
        false
    )

    const addProductState = useReactiveVar(addProductDialogState)

    const history = useHistory()
    const hasErrors = useMemo(
        () =>
            !!Object.values(addProductState.errors ?? {}).some(
                (value) => !!value
            ),
        [addProductState.errors]
    )

    const [createdListingId, setCreatedListingId] = useState<
        string | undefined
    >('')

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const redirectToListingPage = useCallback(
        (listingId: string) => {
            history.push({
                ...history.location,
                pathname: `/bpm/products/product/listing/${listingId}`,
            })
        },
        [history]
    )

    const onCompleteSaveProprietaryCategoriesByFamily = useCallback(() => {
        showGlobalSnackbar({
            variables: {
                message: ADD_PROPRIETARY_CATEGORY_SUCCESS_MESSAGE,
                variant: 'success',
                id: 'ProductListing_NewListingSuccess_CategoryByFamily',
            },
        })
        setDialogOpen(false)
        redirectToListingPage(createdListingId ?? '')
    }, [createdListingId, redirectToListingPage, showGlobalSnackbar])

    const [
        saveProprietaryCategoriesByFamily,
        { loading: newCategoriesToFamilyLoading },
    ] = useMutation(SaveProprietaryCategoriesByFamily, {
        onCompleted: () => {
            onCompleteSaveProprietaryCategoriesByFamily()
        },
        onError: (error: ApolloError) => {
            let errMsg = error.message ?? ADD_PROPRIETARY_CATEGORY_ERROR_MESSAGE
            if (error.message.includes('already exists'))
                errMsg = DUPLICATE_PRODUCT_MESSAGE

            showGlobalSnackbar({
                variables: {
                    message: errMsg,
                    variant: 'error',
                    id: 'ProductListing_Detail_CategoryToFamily_UpdateError',
                },
            })
            return null
        },
    })

    const [
        createProductListingDetail,
        { loading: newProductListingDetailLoading },
    ] = useMutation<
        { createProductListingDetail: Partial<ProductListingDetail> },
        { saveProductListingDetailInput: Partial<ProductListingDetailInput> }
    >(CreateProductListingDetail, {
        onCompleted: ({
            createProductListingDetail,
        }: {
            createProductListingDetail: Partial<ProductListingDetail>
        }) => {
            const {
                listingId,
                sectionNumber,
                sectionTitle,
                categories,
            } = createProductListingDetail

            setCreatedListingId(listingId)
            if (
                addProductState.type === ListingLevel.PremiumProprietary &&
                addProductState.productTypeRadio === RadioType.New
            ) {
                saveProprietaryCategoriesByFamily({
                    variables: {
                        input: {
                            sectionNumber,
                            sectionTitle,
                            category: categories ?? [],
                            meta: {
                                addedBy: firmId,
                                addedDate: new Date().toDateString(),
                            },
                        },
                    },
                })
            } else {
                showGlobalSnackbar({
                    variables: {
                        message: ADD_PRODUCT_SUCCESS_MESSAGE,
                        variant: 'success',
                        id: 'ProductListing_NewListingSuccess',
                    },
                })
                setDialogOpen(false)
                addProductState.type === ListingLevel.PremiumProprietary
                    ? setTimeout(() => {
                          redirectToListingPage(listingId ?? '')
                      }, 0)
                    : redirectToListingPage(listingId ?? '')
            }
        },
        onError: (error: ApolloError) => {
            let erMsg = error.message ?? ADD_PRODUCT_ERROR_MESSAGE
            if (error.message.includes('already exists'))
                erMsg = DUPLICATE_PRODUCT_MESSAGE
            showGlobalSnackbar({
                variables: {
                    message: erMsg,
                    variant: 'error',
                    id: 'ProductListing_DetailUpdateError',
                },
            })
            return null
        },
    })

    const [
        getFirmId,
        { data: firmdetails, loading: isGetFirmIdLoading },
    ] = useLazyQuery<{
        getFirmByDisplayName: { firmId: string; displayName: string }
    }>(FirmIdByDisplayNameQuery, { fetchPolicy: 'cache-first' })

    const [
        getProductTypePremiumPlus,
        { data: allProductTypePremiumPlus, loading: checkingPTPremiumPlus },
    ] = useLazyQuery<ProductSearchResponse>(GetAllProducts, {
        displayName: 'GetAllProducts',
        fetchPolicy: 'network-only',
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on product search',
                    variant: 'error',
                    id: `AddProduct_ProductSearchError`,
                },
            })
        },
    })

    const onDialogClose = useCallback(() => {
        setDialogOpen(false)
    }, [defaultAddProductDialogState, setDialogOpen])

    const handleDialogOpen = useCallback(() => {
        setDialogOpen(true)
    }, [setDialogOpen])

    //Save product info
    const addProduct = useCallback(() => {
        let payload: Record<
            string,
            string | boolean | string[] | null | undefined
        > = {
            isActive: false,
            submissionStatus: ListingStatus.PremiumReview,
        }

        Object.keys(addProductState).forEach((key) => {
            const val = addProductState[key as keyof AddProductForm]
            if (
                val === '' ||
                [
                    'familyRadio',
                    'productTypeRadio',
                    'errors',
                    'isProductNameVerified',
                ].includes(key) ||
                (key === 'categories' && val.length === 0)
            )
                return
            else if (key === 'sectionNames' && val) {
                const sections = (val as string).split('-')
                const sectionNumber = sections?.shift()?.trim()
                const sectionTitle = sections?.join('-')?.trim()
                payload = {
                    ...payload,
                    sectionNumber,
                    sectionTitle: sectionTitle?.trim(),
                }

                return
            } else if (key === 'firmName') {
                payload.firmId =
                    firmdetails?.getFirmByDisplayName?.firmId?.trim() ?? ''
                return
            } else if (key === 'type' && val) {
                const type = addProductState.type
                let submissionStatus = ListingStatus.ProprietaryReview
                if (type === ListingLevel.Premium) {
                    submissionStatus = ListingStatus.PremiumReview
                } else if (type === ListingLevel.ProductListing) {
                    submissionStatus = ListingStatus.InReview
                }
                payload = {
                    ...payload,
                    submissionStatus,
                    type,
                }

                return
            }
            payload[key] = typeof val === 'string' ? val.trim() : val
        })

        if (
            addProductState.productTypeRadio === RadioType.New ||
            isPremiumPlusProductType
        ) {
            payload.isPremiumPlus = true
        }

        createProductListingDetail({
            variables: {
                saveProductListingDetailInput: { ...payload },
            },
        })
    }, [addProductState, isPremiumPlusProductType, firmdetails])

    useEffect(() => {
        if (
            allProductTypePremiumPlus?.listingSearch?.matches &&
            allProductTypePremiumPlus?.listingSearch?.matches.length > 0
        ) {
            setIsPremiumPlusProductType(true)
            return
        }
        setIsPremiumPlusProductType(false)
    }, [allProductTypePremiumPlus])

    useEffect(() => {
        const searchTerm = addProductState.firmName
            ?.split(FIRM_NAME_SEPARATOR)[0]
            ?.slice(1, -1)

        if (searchTerm) {
            getFirmId({
                variables: {
                    firmName: searchTerm ?? '',
                },
            })
        }
    }, [getFirmId, addProductState.firmName])

    useEffect(() => {
        if (
            !!addProductState.sectionNames &&
            !!addProductState.productType &&
            !addProductState?.errors?.productType &&
            !addProductState?.errors?.sectionNames
        ) {
            getProductTypePremiumPlus({
                variables: {
                    manufacturerProductSearchInput: {
                        facets: [
                            {
                                name: 'sectionNames',
                                values: [addProductState.sectionNames],
                            },
                            {
                                name: 'productType',
                                values: [addProductState.productType],
                            },
                            {
                                name: 'isPremiumPlus',
                                values: ['true'],
                            },
                        ],
                    },
                },
            })
        }
    }, [
        getProductTypePremiumPlus,
        addProductState?.sectionNames,
        addProductState?.productType,
        addProductState?.errors?.productType,
        addProductState?.errors?.sectionNames,
    ])

    const loading = useMemo(() => {
        return addProductState.type === ListingLevel.PremiumProprietary
            ? newProductListingDetailLoading || newCategoriesToFamilyLoading
            : newProductListingDetailLoading
    }, [
        addProductState.type,
        newProductListingDetailLoading,
        newCategoriesToFamilyLoading,
    ])

    useEffect(() => {
        if (!dialogOpen) {
            setProductDialogState({ ...defaultAddProductDialogState })
        }
    }, [dialogOpen])

    return (
        <>
            <RoundButton
                hasShadow={false}
                data-testid="addProductButton"
                backgroundColor="#0098D6"
                onClick={handleDialogOpen}
            >
                + Add Product
            </RoundButton>

            <Dialog
                open={dialogOpen}
                onClose={onDialogClose}
                maxWidth="md"
                disableBackdropClick={true}
                className={classes.dialogContainer}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle className={classes.title}>
                    <Typography variant="h6" component="span">
                        Add Product Listing
                    </Typography>
                    <IconButton
                        className={classes.closeButton}
                        data-testid="close-btn"
                        aria-label="close"
                        size="small"
                        onClick={onDialogClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.dialogcontent}>
                    <GridContainer item>
                        <AddProduct />
                    </GridContainer>
                </DialogContent>
                <DialogActions className={classes.actionContainer}>
                    <Button
                        data-testid="apply-button"
                        className={classes.addProductButton}
                        onClick={addProduct}
                        disabled={
                            !!isGetFirmIdLoading ||
                            !!hasErrors ||
                            !!checkingPTPremiumPlus ||
                            !addProductState.isProductNameVerified
                        }
                    >
                        Add Product
                    </Button>
                </DialogActions>
                <Backdrop className={classes.backdrop} open={loading}>
                    {<CircularProgress color="inherit" />}
                </Backdrop>
            </Dialog>
        </>
    )
}

export default AddNewProductModal
